/**
 * PraticaStatoDetailPage — F-005 Phase 5 (Task 5.4).
 *
 * Single-pratica detail view. Refined from the sketch
 * `arness-sketches/pratica-status/SketchPraticaStatoDetail.tsx`: the static mock
 * data is replaced with the real `usePraticaStatus` polling hook, and the inline
 * banner + timeline are now the extracted `ErrorSegnaloBanner` /
 * `PraticaTimeline` components. The error banner renders only when the pratica
 * is `BLOCCATA`.
 *
 * Like the list page, the inner UI is wrapped in the feature-scoped
 * `QueryClientProvider` (`praticaStatusQueryClient`) — the app has no global
 * QueryClient, so the hooks would otherwise throw "No QueryClient set".
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { StateBadge } from '../components/StateBadge';
import { PraticaTimeline } from '../components/PraticaTimeline';
import { ErrorSegnaloBanner } from '../components/ErrorSegnaloBanner';
import { usePraticaStatus } from '../hooks/usePraticaStatus';
import { praticaStatusQueryClient } from '../lib/query-client';

export function PraticaStatoDetailPage() {
  return (
    <RequireAuth>
      <AppLayout>
        <QueryClientProvider client={praticaStatusQueryClient}>
          <PraticaStatoDetailInner />
        </QueryClientProvider>
      </AppLayout>
    </RequireAuth>
  );
}

function PraticaStatoDetailInner() {
  const { id } = useParams<{ id: string }>();
  const { data: pratica, isLoading, isError } = usePraticaStatus(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  if (isError || !pratica) {
    return (
      <div className="py-8 text-center text-destructive" role="alert">
        Pratica non trovata o accesso non autorizzato.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Stato pratica</h1>
        <StateBadge stato={pratica.stato} />
      </div>

      {pratica.stato === 'BLOCCATA' && (
        <ErrorSegnaloBanner
          praticaId={pratica.id}
          operatorNote={pratica.operatorNoteVisible}
          contestedFields={pratica.contestedFields}
        />
      )}

      <section className="space-y-3" aria-labelledby="timeline-heading">
        <h2 id="timeline-heading" className="text-base font-semibold">
          Cronologia
        </h2>
        <PraticaTimeline events={pratica.timeline} />
      </section>
    </div>
  );
}
